'use client';

import Icon from '@/components/ui/AppIcon';

interface Props {
  userName: string;
  aiBriefing: string | null;
  briefingLoading: boolean;
  onRefreshBriefing: () => void;
}

export default function Row1ExecHero({
  userName,
  aiBriefing,
  briefingLoading,
  onRefreshBriefing,
}: Props) {
  const dateLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div
      className="rounded-xl p-6 md:p-8 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #000150 0%, #033494 55%, #07B0E9 100%)',
        boxShadow: '0 8px 32px rgba(0,1,80,0.25)',
      }}
    >
      {/* Decorative circles */}
      <div
        className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #07B0E9, transparent)',
          transform: 'translate(30%, -30%)',
        }}
      />
      <div
        className="absolute bottom-0 left-1/3 w-48 h-48 rounded-full opacity-5 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #FFFFFF, transparent)',
          transform: 'translateY(40%)',
        }}
      />

      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-4">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{
              backgroundColor: 'rgba(7,176,233,0.2)',
              border: '1px solid rgba(7,176,233,0.4)',
              color: '#07B0E9',
            }}
          >
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            SmartPick™ AI Executive Briefing
          </div>
          <span className="text-white/40 text-xs">{dateLabel}</span>
        </div>

        {briefingLoading ? (
          <div className="space-y-3 max-w-5xl">
            <div className="h-4 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.15)', width: '90%' }} />
            <div className="h-4 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.12)', width: '75%' }} />
            <div className="h-4 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.10)', width: '82%' }} />
            <div className="flex items-center gap-2 mt-2">
              <div className="w-3 h-3 rounded-full border border-cyan-400/50 border-t-cyan-400 animate-spin" />
              <span className="text-xs text-white/40">Generating AI briefing from live data…</span>
            </div>
          </div>
        ) : aiBriefing ? (
          <p className="text-white text-base md:text-lg leading-relaxed font-light max-w-5xl">
            <span className="font-semibold text-white">Good morning, {userName}.</span> {aiBriefing}
          </p>
        ) : (
          <p className="text-white text-base md:text-lg leading-relaxed font-light max-w-5xl">
            <span className="font-semibold text-white">Good morning, {userName}.</span>{' '}
            NextRoof 360 briefing unavailable — check live data sources.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 mt-5">
          <div className="flex items-center gap-1.5 text-xs text-white/60">
            <Icon name="ClockIcon" size={13} />
            Updated just now
          </div>
          <div className="flex items-center gap-1.5 text-xs text-white/60">
            <Icon name="CpuChipIcon" size={13} />
            {briefingLoading ? 'Generating…' : 'AI-powered · Live data'}
          </div>
          <div className="flex items-center gap-1.5 text-xs" style={{ color: '#10B981' }}>
            <Icon name="CheckCircleIcon" size={13} />
            All 6 departments reporting
          </div>
          {aiBriefing && !briefingLoading && (
            <button
              onClick={onRefreshBriefing}
              className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 transition-colors"
            >
              <Icon name="ArrowPathIcon" size={12} />
              Refresh
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
